import { useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { azureDevOpsApiRef } from '@backstage/plugin-azure-devops';
import { BuildResult, BuildRun, BuildStatus } from '@backstage/plugin-azure-devops-common';
import { InfoCard, ResponseErrorPanel, Table, TableColumn } from '@backstage/core-components';
import { Box, Grid, Link, Typography } from '@material-ui/core';
import { useAsync } from 'react-use';

const AZURE_PROJECT_NAME = 'Zest Development';
const AZURE_HOST = 'dev.azure.com';
const AZURE_ORG = 'ZestEco';
const AZURE_PERMISSION_ENTITY_REF = 'component:default/azure-devops';

type PipelineRunRow = {
  id: number;
  title: string;
  requestedBy: string;
  branch: string;
  status: string;
  result: string;
  queuedAt: string;
  queuedAtSort: number;
  link?: string;
};

const STATUS_LABELS: Record<number, string> = {
  [BuildStatus.None]: 'None',
  [BuildStatus.InProgress]: 'In Progress',
  [BuildStatus.Completed]: 'Completed',
  [BuildStatus.Cancelling]: 'Cancelling',
  [BuildStatus.Postponed]: 'Postponed',
  [BuildStatus.NotStarted]: 'Not Started',
};

const RESULT_LABELS: Record<number, string> = {
  [BuildResult.None]: 'None',
  [BuildResult.Succeeded]: 'Succeeded',
  [BuildResult.PartiallySucceeded]: 'Partially Succeeded',
  [BuildResult.Failed]: 'Failed',
  [BuildResult.Canceled]: 'Canceled',
};

const columns: TableColumn<PipelineRunRow>[] = [
  {
    title: 'Pipeline',
    field: 'title',
    render: row =>
      row.link ? (
        <Link href={row.link} target="_blank" rel="noopener noreferrer">
          {row.title}
        </Link>
      ) : (
        row.title
      ),
  },
  { title: 'Requested By', field: 'requestedBy' },
  { title: 'Branch', field: 'branch' },
  { title: 'Status', field: 'status' },
  { title: 'Result', field: 'result' },
  { title: 'Queued', field: 'queuedAt', defaultSort: 'desc' },
];

const formatDate = (value?: string) => {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
};

const parseDateSort = (value?: string) => {
  if (!value) {
    return 0;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const toBranchName = (source?: string) => {
  if (!source) {
    return 'Unknown';
  }

  const branch = source.split(' ')[0];
  return branch.replace('refs/heads/', '');
};

const toRow = (build: BuildRun): PipelineRunRow | undefined => {
  const id = build.id;
  if (!id) {
    return undefined;
  }

  const status = build.status !== undefined ? STATUS_LABELS[build.status] ?? String(build.status) : 'Unknown';
  const result = build.result !== undefined ? RESULT_LABELS[build.result] ?? String(build.result) : 'Unknown';

  return {
    id,
    title: build.title,
    requestedBy: build.uniqueName ?? 'Unknown',
    branch: toBranchName(build.source),
    status,
    result,
    queuedAt: formatDate(build.queueTime),
    queuedAtSort: parseDateSort(build.queueTime),
    link: build.link,
  };
};

type RunSummary = {
  total: number;
  succeeded: number;
  failed: number;
  completed: number;
  successRate: number;
};

const buildSummary = (runs: PipelineRunRow[]): RunSummary => {
  const succeeded = runs.filter(run => run.result === 'Succeeded').length;
  const failed = runs.filter(run => run.result === 'Failed').length;
  const completed = runs.filter(run => run.status === 'Completed').length;
  const total = runs.length;
  const denominator = succeeded + failed;
  const successRate = denominator > 0 ? (succeeded / denominator) * 100 : 0;

  return {
    total,
    succeeded,
    failed,
    completed,
    successRate,
  };
};

const getLast24HoursRuns = (runs: PipelineRunRow[]): PipelineRunRow[] => {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return runs.filter(run => run.queuedAtSort >= cutoff);
};

export const AzurePipelineRunsPage = () => {
  const azureDevOpsApi = useApi(azureDevOpsApiRef);
  const permissionEntityRef = process.env.NODE_ENV === 'development' ? '' : AZURE_PERMISSION_ENTITY_REF;

  const { value, loading, error } = useAsync(
    () =>
      azureDevOpsApi.getBuildRuns(
        AZURE_PROJECT_NAME,
        permissionEntityRef,
        undefined,
        undefined,
        AZURE_HOST,
        AZURE_ORG,
        { top: 100 },
      ),
    [azureDevOpsApi, permissionEntityRef],
  );

  const rows = useMemo(() => {
    return (value?.items ?? [])
      .map(toRow)
      .filter((row): row is PipelineRunRow => Boolean(row))
      .sort((a, b) => b.queuedAtSort - a.queuedAtSort);
  }, [value]);

  const last24HoursRuns = useMemo(() => getLast24HoursRuns(rows), [rows]);
  const summary = useMemo(() => buildSummary(last24HoursRuns), [last24HoursRuns]);

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <InfoCard title="Pipeline Runs">
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box p={2} border={1} borderColor="grey.300" borderRadius={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Successful Runs
              </Typography>
              <Typography variant="h5">{summary.succeeded}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box p={2} border={1} borderColor="grey.300" borderRadius={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Failed Runs
              </Typography>
              <Typography variant="h5">{summary.failed}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box p={2} border={1} borderColor="grey.300" borderRadius={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Success Rate
              </Typography>
              <Typography variant="h5">{summary.successRate.toFixed(1)}%</Typography>
            </Box>
          </Grid>
        </Grid>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            Based on last 24 hours: {summary.total} runs ({summary.completed} completed).
          </Typography>
        </Box>
      </Box>
      <Table
        title={`Azure DevOps - ${AZURE_PROJECT_NAME}`}
        isLoading={loading}
        options={{ search: true, paging: true, pageSize: 20 }}
        columns={columns}
        data={rows}
      />
    </InfoCard>
  );
};